import { ProductFlowEmployeeType } from "@/components/product-manager-components/ProductFlowEmployeeType";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import DateUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/DateUtils";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { LazoProduct } from "@/models/LazoProduct";
import { ClientMyServicesResponseDTO, ClientWorkflowSummaryDTO } from "@/models/one/ClientMyServices";
import { BookkeepingFlowCreationRequest } from "@/types/BookkeepingFlowCreationRequest";
import { BookkeepingFlowCreationRequestV2 } from "@/types/BookkeepingFlowCreationRequestV2";
import { ProductManagerService } from "@/types/ProductManagerService";
import { ProductManagerServicesFilters } from "@/types/ProductManagerServicesFilters";
import { ProductModule } from "@/types/ProductModule";
import { ProductService, ProductServiceOption } from "@/types/ProductService";

class LazoProductService {
  path: string;

  constructor(path: string) {
    this.path = path;
  }

  async getAllProducts(): Promise<ApiResponseV2<LazoProduct[]>> {
    return HttpClientV2.get(`${this.path}/all`);
  }

  async filterProductFlows(
    filter: ProductManagerServicesFilters,
    { orderBy, direction }: { orderBy: string; direction: "ASC" | "DESC" | "asc" | "desc" },
    page: number = 0,
    pageSize: number = 10,
    showAll: boolean
  ): Promise<ApiResponseV2<IPageV2<ProductManagerService>>> {
    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("pageSize", pageSize.toString());
    params.set("showAll", showAll.toString());
    params.set("orderBy", orderBy);
    params.set("direction", direction);

    return HttpClientV2.post(`${this.path}/filter`, filter, { axiosConfig: { params } });
  }

  async createBookkeepingFlow(request: BookkeepingFlowCreationRequest): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/create-bookkeeping-flow`, request);
  }

  async createBookkeepingFlowV2(request: BookkeepingFlowCreationRequestV2, isFirstWorkflow: boolean): Promise<ApiResponseV2<string>> {
    const params = new URLSearchParams();
    params.set("isFirstWorkflow", isFirstWorkflow.toString());

    return HttpClientV2.post(`${this.path}/create-bookkeeping-flow-v2`, request, { axiosConfig: { params, timeout: 15000 } });
  }

  async getProductsOfTenant(tenantExternalId: string): Promise<ApiResponseV2<LazoProduct[]>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}`);
  }

  async getProductsOfTenantForPortal(tenantExternalId: string): Promise<ApiResponseV2<LazoProduct[]>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/portal`);
  }

  async copyProductFlow(productFlowExternalId: string, startDate: Date | null, endDate: Date | null): Promise<ApiResponseV2<string>> {
    const params = new URLSearchParams();
    params.set("productFlowExternalId", productFlowExternalId);
    if (startDate) params.set("startDate", DateUtils.formatDate(startDate, "YYYY-MM-DD"));
    if (endDate) params.set("endDate", DateUtils.formatDate(endDate, "YYYY-MM-DD"));

    return HttpClientV2.post(`${this.path}/copy`, null, { axiosConfig: { params } });
  }

  async updateDefaultAssignedUser(
    lazoProductExternalId: string,
    employeeType: ProductFlowEmployeeType,
    userExternalId: string
  ): Promise<ApiResponseV2<string>> {
    const params = new URLSearchParams();
    params.set("userExternalId", userExternalId);
    params.set("employeeType", employeeType);

    return HttpClientV2.patchWithoutPayload(`${this.path}/${lazoProductExternalId}`, { axiosConfig: { params } });
  }

  async updateRequiresWorkflow(lazoProductExternalId: string, requireWorkflow: boolean): Promise<ApiResponseV2<boolean>> {
    const params = new URLSearchParams();
    params.set("requiresWorkflow", requireWorkflow.toString());

    return HttpClientV2.patchWithoutPayload(`${this.path}/${lazoProductExternalId}`, { axiosConfig: { params } });
  }

  // TODO: Someday create a type for Record<ProductModule, ProductServiceOption[]>, see where to put it and reference it where needed
  async getProductServiceMap(): Promise<ApiResponseV2<Record<ProductModule, ProductServiceOption[]>>> {
    return HttpClientV2.get(`${this.path}/services-map`);
  }

  async getNoWorkflowServices(): Promise<ApiResponseV2<ProductService[]>> {
    return HttpClientV2.get(`${this.path}/no-workflow-services`);
  }

  async getMyServices(tenantExternalId: string): Promise<ApiResponseV2<ClientMyServicesResponseDTO>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/my-services`);
  }

  async getClientServiceWorkflows(tenantExternalId: string, productExternalId: string): Promise<ApiResponseV2<ClientWorkflowSummaryDTO[]>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/my-services/${productExternalId}/workflows`);
  }

  async markProductAsSeen(tenantExternalId: string, productExternalId: string): Promise<ApiResponseV2<void>> {
    return HttpClientV2.post(`${this.path}/tenant/${tenantExternalId}/my-services/${productExternalId}/mark-seen`, null);
  }
}

export default new LazoProductService("/api/product");
